import { useMemo } from "react";
import {
  PrivateImplementationMeta,
  useQueryPrivateImplementationsMeta,
} from "./useQueryPrivateImplementationsMeta";
import { ImplementationMeta } from "./useQueryImplementationsMeta";

interface Input {
  searchedImplementationMetas: ImplementationMeta[];
  onlyAppsWithAuth?: boolean;
}

/** This hook takes an array of implementationMetas from the search endpoint and hydrates them with
 * an implementation meta object from the `/api/v4/implementations-meta/private endpoint.
 * This object is used for rendering app implementation details like names and images,
 * and determining authentication details for that app
 */

export function useHydrateImplementationsWithImplementationsMeta({
  searchedImplementationMetas,
  onlyAppsWithAuth,
}: Input) {
  // Build a unique list of currentImplementationIds from the apps
  const currentImplementationIds = useMemo(() => {
    const auths = searchedImplementationMetas.reduce(
      (accumulator, implementation) => {
        if (!implementation?.id) {
          return accumulator;
        }
        const key = implementation.id;
        accumulator[key] = true;
        return accumulator;
      },
      {} as Record<string, boolean>,
    );
    return Object.keys(auths);
  }, [searchedImplementationMetas]);

  // Query for implementationsMeta for the currentImplementationIds
  const { implementationsMeta, isLoading } = useQueryPrivateImplementationsMeta(
    {
      selectedApis: currentImplementationIds,
    },
    {
      enabled: searchedImplementationMetas.length > 0,
    },
  );

  // Build a hash of the implementationsMeta by currentImplementationId
  const implementationsMetaHash = useMemo(() => {
    return implementationsMeta.reduce(
      (accumulator, imp) => {
        if (!imp.id) {
          return accumulator;
        }
        const key = imp.id;
        accumulator[key] = imp;
        return accumulator;
      },
      {} as Record<string, PrivateImplementationMeta>,
    );
  }, [implementationsMeta]);

  // Hydrate apps with implementation objects
  let hydratedImplementationMetas = searchedImplementationMetas.map(
    (implementation) => {
      const key = implementation?.id;
      return {
        ...implementation,
        implementationMeta: key ? implementationsMetaHash[key] : undefined,
      };
    },
  );

  // This allows us to optionally filter out apps that don't have an authType, and
  // therefore shouldn't show in the Typeahead when the user creates a new connection
  if (onlyAppsWithAuth) {
    hydratedImplementationMetas = hydratedImplementationMetas.filter(
      (implementation) => implementation.implementationMeta?.authType,
    );
  }

  return {
    hydratedImplementationMetas,
    isHydrating: isLoading,
  };
}
